/**
 * Ordering the catalog (FR-3.5).
 *
 * Six keys, each in both directions. The derived fields on `ItemRecord` exist
 * for this: the derived fields are denormalized precisely so that
 * sorting does not have to walk every use record (data model §3).
 *
 * Two rules run through all of it.
 *
 * **A missing value sorts last, whichever direction is asked for.** An item with
 * no price is not a cheap item and an item never used is not an item used long
 * ago; putting them at whichever end the comparison happens to produce would
 * make the top of the list meaningless in one direction and misleading in the
 * other. They go to the bottom either way, where "not recorded" reads as what it
 * is.
 *
 * **The order is total.** Every comparison falls back to name and then to id, so
 * two items with the same price do not swap places between renders -- which they
 * would with an unstable tiebreak, and which looks like a bug rather than like
 * sorting.
 */
import { fieldValue } from './merge.js';
import { decode } from './hlc.js';
/**
 * By category, A to Z.
 *
 * It was newest-first, on the reasoning that what someone opening the catalog
 * most often wants is what they just added -- which is true while a catalog is
 * being built and stops being true once it is built. A manifest is read to find
 * things and to see what is in a category; both are easier when the things in a
 * category are together.
 *
 * It costs the one property newest-first had: a creation clock is never missing,
 * and a category can be. An uncategorized item sorts last, as every missing
 * value does, rather than at the top where it would be read as a heading.
 */
export const DEFAULT_SORT = { key: 'category', direction: 'asc' };
/**
 * FR-3.5 names "value"; this offers the two the data model actually holds.
 *
 * Split rather than conflated because they answer different questions, and
 * because `NFR-SAFE-1` forbids presenting an estimate as authoritative -- a
 * single "value" option would have to silently pick one, and whichever it picked
 * would be wrong half the time.
 */
export const SORT_KEYS = [
    { key: 'category', label: 'category' },
    { key: 'addedAt', label: 'date added' },
    { key: 'name', label: 'name' },
    { key: 'acquiredOn', label: 'acquisition date' },
    { key: 'purchasePrice', label: 'purchase price' },
    { key: 'estimatedValue', label: 'estimated value' },
];
/**
 * A comparable number, or null when the item has no value for this key.
 *
 * Money is compared by minor units across currencies, which is not a
 * conversion and is not shown to anyone: there are no exchange rates here and
 * there will not be. It exists so "most expensive first" has a defined meaning
 * in a catalog holding two currencies, the same compromise `rankOf` makes for
 * ranking report groups. The displayed figures stay in their own currency.
 */
function numeric(item, key) {
    switch (key) {
        case 'addedAt': {
            if (item.createdHlc === null)
                return null;
            try {
                return decode(item.createdHlc).millis;
            }
            catch {
                return null;
            }
        }
        case 'purchasePrice':
            return minorOf(fieldValue(item, 'purchasePrice'));
        case 'estimatedValue':
            return minorOf(fieldValue(item, 'estimatedValue'));
        default:
            return null;
    }
}
const minorOf = (amount) => amount === null || amount === undefined ? null : amount.minor;
/**
 * One collator for the whole module, rather than one per comparison.
 *
 * `String.prototype.localeCompare` with options builds a collator on each call.
 * At the reference workload that made sorting by name 121 ms and, at the 20,000
 * items risk R7 names, 514 ms -- past NFR-PERF-3's 300 ms budget on a key people
 * use constantly. Reusing one instance makes it roughly twenty times cheaper for
 * identical results.
 *
 * `numeric` so "Box 10" follows "Box 9", and `sensitivity: 'base'` so case and
 * accents do not decide the order.
 */
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
/**
 * Text that has to be ordered the way a reader reads it, which only names are.
 *
 * Calendar dates are deliberately *not* collated: `YYYY-MM-DD` sorts
 * chronologically under plain byte comparison, which is part of why dates are
 * stored that way (NFR-FUNC-4). Running them through a collator was both slower
 * and less predictable -- numeric collation sees three numbers separated by
 * punctuation rather than a date.
 */
function collated(item, key, category) {
    if (key === 'category')
        return category?.(item) ?? null;
    if (key !== 'name')
        return null;
    const name = fieldValue(item, 'name');
    return typeof name === 'string' && name !== '' ? name : null;
}
/** A comparable string, or null. Calendar dates compare correctly as strings. */
function textual(item, key) {
    switch (key) {
        case 'acquiredOn': {
            const on = fieldValue(item, 'acquiredOn');
            return typeof on === 'string' && on !== '' ? on : null;
        }
        default:
            return null;
    }
}
/** Name then id: the same two items never swap places between renders. */
const byIdentity = (a, b) => {
    const names = collator.compare(collated(a, 'name') ?? '', collated(b, 'name') ?? '');
    return names !== 0 ? names : a.itemId < b.itemId ? -1 : a.itemId > b.itemId ? 1 : 0;
};
export function sortItems(items, sort = DEFAULT_SORT, 
/**
 * Required to sort by category, and ignored otherwise.
 *
 * Without it every item reports no category, so they all sort as missing and
 * fall through to the name-then-id order every comparison ends with. That is
 * the same thing a catalog with no categories at all would do, which is why it
 * degrades rather than throws.
 */
category) {
    const sign = sort.direction === 'asc' ? 1 : -1;
    return [...items].sort((a, b) => {
        const left = collated(a, sort.key, category) ?? textual(a, sort.key) ?? numeric(a, sort.key);
        const right = collated(b, sort.key, category) ?? textual(b, sort.key) ?? numeric(b, sort.key);
        // Missing sorts last regardless of direction, so the sign is not applied.
        if (left === null && right === null)
            return byIdentity(a, b);
        if (left === null)
            return 1;
        if (right === null)
            return -1;
        let order;
        if (typeof left === 'string' && typeof right === 'string') {
            order =
                sort.key === 'name'
                    ? collator.compare(left, right)
                    : // An ordinal comparison, because these are `YYYY-MM-DD`.
                        left < right
                            ? -1
                            : left > right
                                ? 1
                                : 0;
        }
        else {
            order = Number(left) - Number(right);
        }
        return order !== 0 ? order * sign : byIdentity(a, b);
    });
}
/** The direction to offer first for a key, since "best" differs by what is being sorted. */
export const naturalDirection = (key) => 
// Dates and money read most usefully largest-first: the newest thing, the most
// expensive thing. A name, and a category name, read A to Z.
key === 'name' || key === 'category' || key === 'acquiredOn' ? 'asc' : 'desc';
//# sourceMappingURL=sort.js.map